/**
 * Drive a local match run from React: file in, progress and result out.
 *
 * Owns the Worker's lifecycle so no screen has to. The rules that matter:
 *
 *   • **One run at a time.** Starting a second while one is in flight terminates the first
 *     rather than racing it — two runs posting `done` into the same state would leave whichever
 *     finished last as the visible answer, which may not be the one the visitor asked for.
 *   • **Terminated on unmount.** A Worker outlives the component that created it, so navigating
 *     away mid-run would leave it burning a core until the tab closes.
 *   • **`capabilityError` is distinct from `error`.** "Your browser cannot do this" and "this run
 *     failed" need different copy and different next steps (F15 IA 37), so they are different
 *     states rather than one string.
 *   • **Mapping is confirmed before matching.** `load` parses and proposes; `start` runs. They are
 *     two calls, not one, because a mapping is a *guess about someone else's data* — mapping the
 *     wrong column to `email` produces a confidently wrong duplicate count, which is the exact
 *     failure this product exists to prevent. A single call that inferred and ran would present
 *     that guess as an answer, with no point at which anyone could disagree with it.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { MatchResult } from '@dedupr/match-core';
import { parseCsv, toRecords, type ParsedCsv } from './csv.js';
import { persistRun } from './persist.js';
import { adjudicatePersistedRun, type LocalAdjudicationOutcome } from './adjudicate.js';
import { looksLikeXlsx, parseXlsx } from './xlsx.js';
/* The mapping comes from the provider registry, so a Salesforce or HubSpot export is recognised as
   one and mapped by that system's own field names — the same descriptor the relay's pull path uses. */
import {
  applyTemplate,
  headerSignature,
  overrideMapping,
  proposeMapping,
  templateFrom,
  type CanonicalField,
  type MappingProposal,
} from '@dedupr/connectors';
import type { RunRequest, WorkerResponse } from './matchWorker.js';
import { MAX_RECORDS, openLocalStore, type StoredRecord } from './store.js';
import { capacityFor, explainCapacity, type Capacity } from './capacity.js';

/**
 * Pull the engine's Worker chunk into the service-worker cache before it is needed.
 *
 * ⚠️ **A real offline fix, not an optimisation.** The Worker is a separate on-demand chunk, so a
 * visitor who installed the app and then lost connectivity had the shell, the bundle and the fonts
 * cached — and **not the engine**. Recognition still worked and the run silently produced nothing, so
 * "works offline" was false for the only feature that matters. Found by killing the dev server and
 * running a real file through it.
 *
 * ⚠️⚠️ **Constructed and terminated, rather than `fetch`ed via a hoisted URL — and that detail is
 * load-bearing.** The first attempt lifted `new URL('./matchWorker.ts', import.meta.url)` into a
 * module constant so it could be fetched. Vite's worker plugin only recognises that expression
 * **inline inside `new Worker(...)`**; hoisted, it fell through to the asset pipeline and was inlined
 * as a `data:video/mp2t` base64 URL of the **raw, uncompiled TypeScript**. `new Worker(thatURL)`
 * cannot run — so the engine was completely broken in the production build, with a green test suite,
 * and it was visible only by reading the built bundle. Spawning the real Worker fetches the real
 * chunk and cannot drift from how it is constructed for actual use.
 */
export function warmMatchEngine(): void {
  try {
    // Terminated immediately: the point is the network fetch the construction performs.
    new Worker(new URL('./matchWorker.ts', import.meta.url), { type: 'module' }).terminate();
  } catch {
    // Costs offline matching and nothing else — the online path fetches on demand as before.
  }
}

export type LocalPhase =
  | 'idle'
  | 'parsing'
  /** Parsed, mapping proposed, waiting for a human to confirm or change it. */
  | 'mapping'
  | 'matching'
  | 'done'
  | 'error';

export interface LocalMatchState {
  phase: LocalPhase;
  /** 0–1 during `matching`. */
  progress: number;
  parsed: ParsedCsv | null;
  mapping: MappingProposal | null;
  result: MatchResult | null;
  /** The file's own name, so the run can be stored as a named source. */
  fileName: string | null;
  /**
   * Whether the finished run reached IndexedDB.
   *
   * ⚠️ Surfaced rather than assumed. Until Z-0 nothing persisted at all, and the failure mode of a
   * silent save is the worst one available: the visitor closes the tab believing their work is kept.
   */
  saved: 'pending' | 'saved' | 'failed' | null;
  /** The persisted source's id once `saved`, so the export can be rebuilt from the store. */
  savedSourceId: string | null;
  /**
   * What F15-FR-5's adjudication did with this run's ambiguous band, or `null` when it has not
   * finished (or the run has not been saved).
   *
   * ⚠️ **`skipped: 'off'` is a real outcome, not an absence** — it is how the screen can say "the
   * band went to review because adjudication is off on this device" rather than showing nothing and
   * leaving somebody to conclude the feature is broken.
   */
  adjudication: LocalAdjudicationOutcome | null;
  /**
   * How many records from EARLIER files were included in this run. The match runs over everything
   * on the device, and the run screen has to say so — a group count that quietly includes records
   * from a file loaded last week would otherwise look wrong against the file just chosen.
   */
  matchedWith: number | null;
  /** The rows, kept so `start` can apply a mapping the visitor may have edited. */
  error: string | null;
  /** Set when the browser itself cannot run local mode — see IA 37. */
  capabilityError: string | null;
  /**
   * Set when the file is larger than this build can process, so only the first `MAX_RECORDS` rows
   * were kept.
   *
   * ⚠️ **Separate from `error`, and the run still happens.** Refusing a large file outright would
   * turn "we can show you the first 50,000" into "we cannot help you", which is both less useful
   * and less true. Silently truncating would be worse still — a duplicate count over part of a file
   * presented as a count over the file is exactly the confidently-wrong number this product exists
   * to prevent. So: run, and say plainly what was read.
   */
  truncated: { kept: number; total: number } | null;
  /** The cap that was applied and WHY — IA 38 requires the basis, never a bare verdict. */
  capacity: Capacity | null;
}

const INITIAL: LocalMatchState = {
  phase: 'idle',
  progress: 0,
  parsed: null,
  mapping: null,
  result: null,
  error: null,
  capabilityError: null,
  truncated: null,
  capacity: null,
  fileName: null,
  saved: null,
  savedSourceId: null,
  adjudication: null,
  matchedWith: null,
};

/**
 * Whether this browser can run local mode at all.
 *
 * ⚠️ **Names the missing capability rather than saying "unsupported"** — IA 37's requirement.
 * A visitor told "your browser is not supported" can do nothing with that; one told "this needs
 * Web Workers, which your browser has disabled" can.
 *
 * ⚠️ **Deliberately does not probe OPFS, and that is now permanent rather than "not yet".** This
 * said *"nothing in this slice persists… it joins this check when persistence lands"* — and
 * persistence landed (Z-0), as **IndexedDB**, not OPFS. ADR-067's requirement is that the local
 * store mirror the tenant schema, which IndexedDB satisfies; OPFS remains the target only for the
 * Parquet staging that does not exist. So there is nothing for an OPFS probe to gate, and the
 * paragraph promising one contradicted the IndexedDB note twelve lines below it. Corrected
 * 2026-08-02 while verifying [B-113](../../../../docs/platform/TODO.md)'s closure still held.
 */
export function checkCapabilities(): string | null {
  if (typeof Worker === 'undefined') {
    return 'Local mode needs Web Workers, which this browser does not provide or has disabled. Nothing can be processed on your device without them.';
  }
  if (typeof WebAssembly === 'undefined') {
    /* Not used by the shipped pure-TypeScript matcher (ADR-071: `match-core`, no DuckDB-WASM), but
       it is the hard requirement for the container engine's wasm32 target — better to say so before
       a visitor uploads a large file than to fail midway through one. */
    return 'Local mode needs WebAssembly, which this browser does not provide.';
  }
  /* ⚠️ **IndexedDB is deliberately NOT a capability gate**, and it was one for about ten minutes.
     The run persists so a refresh does not lose it, and Safari private browsing and Firefox's
     "never remember history" both refuse IndexedDB outright — but a refused *save* still leaves a
     working match: the file is read, scored and displayed entirely without it. Gating on it would
     turn "your result will not be kept" into "you cannot use local mode at all", which is a much
     larger claim and a false one. `openLocalStore` rejects and the caller degrades instead.
     (The tests caught this the moment it was added, because jsdom has no IndexedDB — a good reason
     to look again at whether it was really a requirement.) */
  /* `File.text()` — the whole read path. Present everywhere the three above are, so this is a
     backstop against a very old browser rather than a case anyone is expected to hit; it costs one
     comparison and turns a `TypeError` deep in the parser into a sentence. */
  if (typeof File === 'undefined' || typeof File.prototype.text !== 'function') {
    return 'Local mode needs a browser that can read a file in the page (File.text). This one is older than Dedupr supports.';
  }
  return null;
}

export function useLocalMatch() {
  const [state, setState] = useState<LocalMatchState>(() => ({
    ...INITIAL,
    capabilityError: checkCapabilities(),
  }));
  const workerRef = useRef<Worker | null>(null);
  /* A render-fresh mirror of `state`, so event handlers (`start`) can read the current parsed file
     and mapping WITHOUT doing work inside a `setState` updater. ⚠️ Updaters must be pure — React
     double-invokes them in dev to enforce exactly that — and both the worker spawn and the Z-0
     persist originally lived inside one. The persist ran twice: one upload showed as "2 files
     loaded, 54 records" on the Dashboard. Found in a browser, invisible to the suite (the test
     renderer ran the updater once). */
  const stateRef = useRef(state);
  stateRef.current = state;

  const terminate = useCallback(() => {
    workerRef.current?.terminate();
    workerRef.current = null;
  }, []);

  // A Worker outlives its creator; without this it keeps running after navigation.
  useEffect(() => terminate, [terminate]);

  const reset = useCallback(() => {
    terminate();
    setState({ ...INITIAL, capabilityError: checkCapabilities() });
  }, [terminate]);

  /**
   * Override one field's column, from the confirmation screen.
   *
   * ⚠️ Re-derives `usable`, so clearing the only identifying column disables the run rather than
   * letting it start and report "no duplicates" — the answer that reads as "your data is clean".
   */
  const remap = useCallback((field: CanonicalField, column: number | null) => {
    setState((s) =>
      s.mapping && s.parsed
        ? { ...s, mapping: overrideMapping(s.mapping, field, column, s.parsed.headers) }
        : s
    );
  }, []);

  /** Parse the file and propose a mapping. Deliberately stops there. */
  const load = useCallback(
    async (file: File) => {
      const capability = checkCapabilities();
      if (capability) {
        setState({ ...INITIAL, capabilityError: capability });
        return;
      }

      // Supersede rather than race — see the header.
      terminate();
      setState({ ...INITIAL, phase: 'parsing' });

      let parsed: ParsedCsv;
      let mapping: MappingProposal;
      try {
        /* ⚠️ Dispatched on the BYTES, not the extension ([B-134](../../../../docs/platform/TODO.md)).
           An export saved as `contacts.xlsx` is sometimes really a CSV, and a `.txt` from a mail
           client is sometimes really a workbook — trusting the name means a confidently wrong
           parse where reading four bytes gives the right answer every time. */
        const buffer = await file.arrayBuffer();
        parsed = looksLikeXlsx(new Uint8Array(buffer.slice(0, 4)))
          ? await parseXlsx(buffer)
          : parseCsv(new TextDecoder().decode(buffer));
        mapping = proposeMapping(parsed.headers);
        /* ── FR-4's saved mapping template ──────────────────────────────────────────────────────
           A file whose headers this client has confirmed a mapping for before is pre-filled from that
           mapping instead of from the automatic proposal — so a monthly export is mapped once rather
           than monthly, and each re-map is one fewer chance to put `email` on the wrong column.

           ⚠️ **Still a proposal, not a decision.** Every field stays changeable and the panel labels a
           saved column differently from a recognised one (`via: 'saved'`), because *"you told us this
           last time"* is a stronger claim than *"we recognised this"* and the person should know which
           they are looking at.

           ⚠️ **A failure here is silently the automatic proposal.** The store can legitimately be
           unavailable (IndexedDB refused — the reverted capability gate's lesson), and a missing saved
           mapping must degrade to mapping the file by hand rather than blocking the upload. */
        try {
          const store = await openLocalStore();
          try {
            const saved = await store.mappingTemplate(headerSignature(parsed.headers));
            if (saved) mapping = applyTemplate(mapping, saved, parsed.headers);
          } finally {
            store.close();
          }
        } catch {
          /* Left as the automatic proposal. */
        }
      } catch (e) {
        setState({
          ...INITIAL,
          phase: 'error',
          error: `That file could not be read: ${e instanceof Error ? e.message : String(e)}`,
        });
        return;
      }

      if (parsed.rows.length === 0) {
        setState({ ...INITIAL, phase: 'error', parsed, mapping, error: 'That file has a header row but no data rows.' });
        return;
      }

      /* ⚠️ **`MAX_RECORDS` was exported and enforced NOWHERE.** The constant existed, its comment
         explained at length why a run that completes beats a bigger run that hangs the tab — and
         nothing read it, so a 200,000-row export would have been parsed into memory and scored in
         TypeScript, which is the exact tab-hang it was written to prevent.
         Truncating here rather than at match time, because this is where the row count is first
         known and where the person can still choose a smaller file instead. */
      /* ⚠️ **The cap is DERIVED per file now, not a constant** (A-3, step 3). Footprint scales
         with row width — measured at ~166 B/row for 4 columns and ~4,657 for 90 — so one number
         cannot be right for both a narrow export and a 90-column Salesforce one. `capacityFor`
         divides the device's own reported memory by the bytes-per-row at THIS file's width, and
         never exceeds `MAX_RECORDS`, so the derivation can only lower the limit. */
      const total = parsed.rows.length;
      const capacity = capacityFor(parsed.headers.length);
      let truncated: { kept: number; total: number } | null = null;
      if (total > capacity.limit) {
        parsed = { ...parsed, rows: parsed.rows.slice(0, capacity.limit) };
        truncated = { kept: capacity.limit, total };
      }

      /* Stops at `mapping` even when nothing is usable, rather than erroring: the confirmation
         screen can show which columns were found and let someone point at the right one, which is a
         recoverable situation. Erroring here would make an unrecognised header layout a dead end. */
      setState({ ...INITIAL, phase: 'mapping', parsed, mapping, truncated, capacity, fileName: file.name });
    },
    []
  );

  /** Apply the confirmed mapping and run the engine. */
  /**
   * Discard the saved mapping for the current file's shape, and re-propose from scratch (FR-4).
   *
   * ⚠️ **Re-proposes rather than merely deleting.** Deleting the stored row would leave the panel
   * showing the saved columns it just forgot, so the person would still be looking at the mapping they
   * asked to be rid of until they reloaded — which reads as the button not working.
   */
  const forgetTemplate = useCallback(async () => {
    const current = stateRef.current;
    if (!current.parsed) return;
    const headers = current.parsed.headers;
    try {
      const store = await openLocalStore();
      try {
        await store.forgetMappingTemplate(headerSignature(headers));
      } finally {
        store.close();
      }
    } catch {
      /* Nothing stored, or no store. Re-proposing below is still the right outcome. */
    }
    setState((cur) => (cur.parsed ? { ...cur, mapping: proposeMapping(cur.parsed.headers) } : cur));
  }, []);

  const start = useCallback(
    async () => {
      const capability = checkCapabilities();
      if (capability) {
        setState({ ...INITIAL, capabilityError: capability });
        return;
      }

      /* Read from the render-fresh ref rather than closing over `state`, so `start` does not need
         to be re-created on every keystroke of a remap — and cannot act on a stale mapping.
         ⚠️ NOT from inside a setState updater: updaters are double-invoked in dev to enforce their
         purity, and spawning the worker in one created a doomed extra worker on every run. */
      const s = stateRef.current;
      if (!s.parsed || !s.mapping || !s.fileName) return;
      if (!s.mapping.usable) {
        /* Refused rather than run. With no identifying column every record is unblockable and the
           run reports zero duplicates — which reads as "your data is clean" and is a lie. */
        setState((cur) => ({
          ...cur,
          phase: 'error',
          error:
            'None of these columns can identify a person. At least one of email, phone, or a name is needed — otherwise every record would be unmatchable and the result would look like "no duplicates" rather than "we could not tell".',
        }));
        return;
      }

      /* ── FR-4: remember this mapping for the next file shaped like it ─────────────────────────
         Saved at the moment it is CONFIRMED — pressing start is the confirmation, and it is the only
         point at which the mapping is known to be the one the person meant. Saving on every keystroke
         of a remap would store half-finished mappings; saving after the run would lose it if the run
         failed, having already had the answer.

         ⚠️ **A failed save never fails the run.** The records are about to be matched either way, and
         losing the convenience of a remembered mapping is a much smaller loss than refusing to
         process the file — the same ordering the sync history follows. */
      void (async () => {
        try {
          const store = await openLocalStore();
          try {
            await store.saveMappingTemplate(
              templateFrom(s.mapping!, s.parsed!.headers, new Date().toISOString())
            );
          } finally {
            store.close();
          }
        } catch {
          /* Not remembered. The next upload proposes automatically, as it did before. */
        }
      })();

      /* ⚠️ **The match runs over everything on the device, not just this file.** The Dashboard says
         so in as many words ("Dedupr matches across everything on this device, not just within one
         file"), and the first version quietly matched only the new upload — the salesforce groups
         vanished from the "current answer" the moment the hubspot file loaded, and the demo's four
         cross-file people were never found. Ids are namespaced under the new source id BEFORE the
         engine sees them, because the result's pairs must be able to reference an old record and a
         new one in the same breath — see persist.ts's header. */
      const sourceId = crypto.randomUUID();
      const fresh: StoredRecord[] = toRecords(s.parsed, s.mapping).map((r) => ({
        id: `${sourceId}:${r.id}`,
        source_id: sourceId,
        full_name: r.full_name ?? null,
        email: r.email ?? null,
        company: r.company ?? null,
        phone: r.phone ?? null,
        domain: r.domain ?? null,
        // V-19b: the worker partitions the run on this — a companies sheet joins the company run.
        kind: r.kind ?? 'person',
        /* ⚠️ **Explicitly null, not omitted.** A spreadsheet carries no provider-supplied modified
           date, creation date, owner reference or active flag — `toRecords` maps the canonical
           identity columns and nothing else — so these four are genuinely absent for a file source
           and every reader treats `null` as `insufficient_data`.
           Writing them out rather than leaving them off is the point: this is the SECOND
           hand-written `StoredRecord` literal (the relay path's was the first, and it silently
           dropped four signals for months). These fields are optional on the type, so an omission
           here is invisible to the compiler — saying `null` makes the absence a decision somebody
           took rather than one nobody noticed. */
        source_modified_at: null,
        created_at: null,
        owner_pk: null,
        active: null,
      }));

      /* Earlier files' records, straight from the store. Degrades to this-file-only when the store
         cannot open (no IndexedDB): a smaller true answer, and the persist will report `failed`
         rather than pretending — the same posture as the storage gate above it. */
      let existing: StoredRecord[] = [];
      try {
        const store = await openLocalStore();
        try {
          existing = await store.allRecords();
        } finally {
          store.close();
        }
      } catch {
        existing = [];
      }

      const sourceOf = new Map<string, string>();
      for (const r of existing) sourceOf.set(r.id, r.source_id);
      for (const r of fresh) sourceOf.set(r.id, sourceId);

      startWorker([...existing, ...fresh], {
        parsed: s.parsed,
        mapping: s.mapping,
        fileName: s.fileName,
        sourceId,
        records: fresh,
        sourceOf,
      });
      setState((cur) => ({
        ...cur,
        phase: 'matching',
        progress: 0,
        result: null,
        error: null,
        matchedWith: existing.length,
      }));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [terminate]
  );

  /**
   * Spawn the Worker over the union of everything on the device plus the new file. Split out so
   * `start` stays a readable sequence. `run` is the snapshot the Z-0 persist needs when `done`
   * arrives — passed in rather than read from state, so the handler never has to reach into a
   * `setState` updater for it.
   */
  function startWorker(
    records: StoredRecord[],
    run: {
      parsed: NonNullable<LocalMatchState['parsed']>;
      mapping: NonNullable<LocalMatchState['mapping']>;
      fileName: string;
      sourceId: string;
      records: StoredRecord[];
      sourceOf: ReadonlyMap<string, string>;
    }
  ) {
      // Supersede rather than race — see the header.
      terminate();

      const worker = new Worker(new URL('./matchWorker.ts', import.meta.url), { type: 'module' });
      workerRef.current = worker;

      worker.onmessage = (event: MessageEvent<WorkerResponse>) => {
        const msg = event.data;
        if (msg.type === 'progress') {
          setState((s) => (s.phase === 'matching' ? { ...s, progress: msg.fraction } : s));
          return;
        }
        // Terminal either way: tear the Worker down before setting state, so a late message
        // cannot arrive after the run is presented as finished.
        terminate();
        if (msg.type === 'done') {
          /* ⚠️ **Z-0: the run is written to IndexedDB here, and nothing did this before.** The
             match ran in memory and the result went to the screen; every other screen in the app
             reads the store, so they all showed their empty states forever.
             Fired OUTSIDE the state updater, on purpose: updaters must be pure and React
             double-invokes them in dev to prove it — the first version persisted inside one and
             wrote every run twice ("2 files loaded" after one upload). The write is asynchronous
             and the screen does not wait for it — the result is already correct on screen — but
             its outcome is reported, because a save the visitor believes happened and did not is
             worse than one that visibly failed. */
          void persistRun({ ...run, result: msg.result })
            .then(async (outcome) => {
              setState((cur) => ({ ...cur, saved: 'saved', savedSourceId: outcome.sourceId }));
              /* ── F15-FR-5: adjudicate the ambiguous band, on this device's chosen terms ────────
                 ⚠️ **After the save reports success, and never able to undo it.** Adjudication is
                 optional and networked; the match is neither. Chaining it before the `saved` state
                 would let a refused gateway present a completed, persisted run as a failed save.
                 ⚠️ Off by default, so for most devices this reads the setting and returns. */
              try {
                const adjudication = await adjudicatePersistedRun(outcome.runId);
                setState((cur) => ({ ...cur, adjudication }));
              } catch {
                /* Never surfaces as a run failure — the run succeeded. A caller that wants to know
                   whether adjudication ran reads `adjudication`, which stays null here. */
              }
            })
            .catch(() => setState((cur) => ({ ...cur, saved: 'failed' })));
          setState((s) => ({ ...s, phase: 'done', progress: 1, result: msg.result, saved: 'pending' }));
        } else {
          setState((s) => ({ ...s, phase: 'error', error: msg.message }));
        }
      };

      worker.onerror = (event) => {
        /* A Worker that fails to *load* reports here rather than through the protocol, so without
           this the UI would sit at `matching` forever with no error and no result. */
        terminate();
        setState((s) => ({
          ...s,
          phase: 'error',
          error: event.message || 'The local match engine could not start in this browser.',
        }));
      };

      worker.postMessage({ type: 'run', records } satisfies RunRequest);
  }

  return { ...state, load, start, remap, reset, forgetTemplate };
}
